import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import { useParams } from 'react-router-dom';
import ProfessionalModal from '../../../register/professional/professionalModal';
import { useProfessionals } from '../../../hooks/useProfessionals';
import { useCovenantCatalog } from '../../../hooks/useCovenantCatalog';
import { useSaveTreatment } from './hooks/useSaveTreatment';

export default function TreatmentsModal({
  open,
  onCloseTreatmentModal,
  onTreatmentSaved,
  usesInsurance,
}) {
  const { patientId } = useParams();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { professionals: comboBoxDentists, refetch: getComboBoxDentists } = useProfessionals();
  const {
    covenants,
    isLoading: isLoadingCovenants,
    error: covenantsError,
  } = useCovenantCatalog(open && usesInsurance);
  const { save: saveTreatmentRequest } = useSaveTreatment(patientId);
  const [selectedDentistId, setSelectedDentistId] = useState('');
  const [isDentistModalOpen, setDentistModalOpen] = useState(false);

  const [description, setDescription] = useState('');
  const [insurance, setInsurance] = useState('');

  useEffect(() => {
    if (covenantsError) {
      setShowErrorMessage(true);
      setErrorMessage(covenantsError);
    }
  }, [covenantsError]);

  useEffect(() => {
    const dentistIdFromUser = sessionStorage.getItem('dentistIdFromUser');
    if (dentistIdFromUser && dentistIdFromUser !== 'null' && dentistIdFromUser !== 'undefined') {
      setSelectedDentistId(dentistIdFromUser);
    }
  }, [open]);

  const saveTreatment = (event) => {
    event.preventDefault();

    if ((usesInsurance && !insurance) || !description.trim() || !selectedDentistId) {
      setShowErrorMessage(true);
      setErrorMessage('Todos os campos são obrigatórios!');
      return;
    }

    const treatment = {
      pacienteId: patientId,
      descricao: description,
      convenioId: insurance ? Number(insurance) : null,
      dentistaId: selectedDentistId,
    };

    setLoading(true);
    saveTreatmentRequest(treatment)
      .then((returnFromApi) => {
        onTreatmentSaved(returnFromApi);
        cleanFields();
        setLoading(false);
      })
      .catch((returnFromApi) => {
        onTreatmentSaved(returnFromApi);
        console.error('Erro ao salvar tratamento:', returnFromApi);
        setLoading(false);
      });
  };

  function cleanFields() {
    setSelectedDentistId('');
    setDescription('');
    setInsurance('');
  }

  function handleNewDentistSaved() {
    getComboBoxDentists();
  }

  return (
    <Dialog open={open} onClose={onCloseTreatmentModal} maxWidth="sm" fullWidth>
      <Loading isToHidden={isLoading === false && isLoadingCovenants === false} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <DialogTitle>Novo Tratamento</DialogTitle>
      <DialogContent dividers>
        <form id="new-treatment-form" onSubmit={saveTreatment}>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item size={12}>
              <TextField
                data-cy="treatment-description"
                fullWidth
                label="Descrição do Tratamento"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
              />
            </Grid>
            {usesInsurance && (
              <Grid item size={6}>
                <FormControl fullWidth>
                  <InputLabel id="convenio-label">Convênio</InputLabel>
                  <Select
                    data-cy="insurance-select"
                    labelId="convenio-label"
                    value={insurance}
                    label="Convênio"
                    onChange={(e) => setInsurance(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Selecione</em>
                    </MenuItem>
                    {covenants.map((covenant) => (
                      <MenuItem key={covenant.id} value={covenant.id}>
                        {covenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item size={usesInsurance ? 6 : 12}>
              <Autocomplete
                options={comboBoxDentists}
                getOptionLabel={(option) => option.nome ?? ''}
                value={comboBoxDentists.find((d) => d.id === selectedDentistId) ?? null}
                onChange={(_, newValue) => setSelectedDentistId(newValue?.id ?? '')}
                noOptionsText="Nenhum profissional encontrado."
                PaperComponent={({ children, ...other }) => (
                  <Paper {...other}>
                    {children}
                    <Box
                      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button fullWidth size="small" onClick={() => setDentistModalOpen(true)}>
                        + Adicionar profissional
                      </Button>
                    </Box>
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField {...params} data-cy="dentist-select" fullWidth label="Profissional" />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseTreatmentModal}>Cancelar</Button>
        <Button
          data-cy="save-treatment-btn"
          type="submit"
          form="new-treatment-form"
          variant="contained"
        >
          Salvar
        </Button>
      </DialogActions>

      <ProfessionalModal
        open={isDentistModalOpen}
        onClose={() => setDentistModalOpen(false)}
        onSave={handleNewDentistSaved}
      />
    </Dialog>
  );
}

TreatmentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCloseTreatmentModal: PropTypes.func.isRequired,
  onTreatmentSaved: PropTypes.func.isRequired,
  usesInsurance: PropTypes.bool,
};

TreatmentsModal.defaultProps = {
  usesInsurance: false,
};
